import {
  ActivitySpinnerSize,
  ActivitySpinnerWidget,
  ButtonType,
  ButtonWidget,
  Img,
  MenuItemInfo,
  Tooltip,
  TooltipPosition
} from '@tableau/widgets';
import { DropdownArrowIcon } from '@tableau/widgets/lib/src/icons/Icons';
import {
  BodyCell,
  DataGrid,
  HeaderColumn
} from '@tableau/widgets-datagrid';
import * as React from 'react';

import { ActionsMenuTestId } from '../../app/spaces/ActionsMenu/ActionsMenu';
import { ExplorerDropdownProps } from '../../app/spaces/Components/ExplorerDropdown';
import { ExplorerDropdownWithoutPropagation } from '../../app/spaces/Components/ExplorerDropdownWithoutPropagation';
import { ExplorerFlyoutPanelProps } from '../../app/spaces/Components/ExplorerFlyoutPanel';
import {
  StyledCellFormatter,
  StyledHeaderFormatter
} from '../../app/spaces/ListView/StyledCellWrapper';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import {
  ButtonControlledFlyoutListMenu,
  ButtonControlledFlyoutListMenuProps
} from '../../common/react/components/ButtonControlledFlyoutListMenu';
import { Icons } from '../../common/react/Icons';
import { AuthorizableType } from '../../permissions/types/AuthorizableType';
import { ICapabilityInfo } from '../../permissions/types/Capabilities';
import { CapabilitySection } from '../../permissions/types/CapabilitySections';
import { PermissionRoleName } from '../../permissions/types/PermissionRole';
import { SiteRolesHelper } from '../../ts/helpers/SiteRolesHelper';
import { IGroup } from '../../ts/ServerApi/types/IGroup';
import { IUser } from '../../ts/ServerApi/types/IUser';
import { PermissionsGridStyles } from '../styles/PermissionsGridStyle';
import {
  IPermission,
  Permission,
  PermissionReason
} from '../types/Permission';
import { GranteeSearchField } from './GranteeSearchField';
import { RowStatus } from './PermissionsGrid';

export namespace PermissionsGridCells {
  function isRowInEditMode(rowStatus: RowStatus): boolean {
    return [RowStatus.EDIT_CLEAN, RowStatus.EDIT_DIRTY, RowStatus.EDIT_DELETE].includes(rowStatus);
  }

  /**
   * Multiple cells need to display dropdowns that can be disabled. ExplorerDropdown internally uses a button as the button
   * part of the dropdown, meaning that when ExplorerDropdown is disabled, internally it is a disabled button. However, even
   * when appearing disabled, it still needs to be capable of minor interactivity, including having a tooltip (registering mouse
   * enter/leave events) and being able to register click events (clicking anywhere in PermissionsGrid can disable a filter that
   * sets some rows to disabled). But disabled buttons do not regiseter mouse events. As a workaround, we are displaying the
   * contents of the button when it is disabled instead of the button itself.
   */
  function renderDropdown(disabled: boolean, button: JSX.Element, dropdownProps: ExplorerDropdownProps): JSX.Element {
    return disabled ?
      <div>{button}</div>
      :
      <span><ExplorerDropdownWithoutPropagation {...dropdownProps} /></span>;
  }

  /** TextHeaderCell - Formatting header cells that display only text */
  export const TEXT_HEADER_TESTID = 'permissions-grid-text-header';

  export function RoleHeaderCell(disabled: boolean): React.StatelessComponent<HeaderColumn.HeaderFormatterProps> {
    return TextHeaderCell(
      disabled,
      {
        marginLeft: `${PermissionsGridStyles.EmptySpaceMargin}px`,
        width: `${PermissionsGridStyles.RoleCellWidth}px`,
      },
    );
  }

  export function TextHeaderCell(disabled: boolean, styleOverrides: React.CSSProperties = {}): React.StatelessComponent<HeaderColumn.HeaderFormatterProps> {
    const className = disabled ? PermissionsGridStyles.DisabledTextStyle : '';
    const textHeaderCellStyleOverrides: React.CSSProperties = {
      ...CommonStyles.Layout.TextEllipsis,
      fontSize: '12px',
      lineHeight: `${PermissionsGridStyles.RowHeight}px`,
      marginRight: CommonStyles.Sizes.MediumSpace,
      ...styleOverrides,
    };
    return StyledHeaderFormatter(
      function TextHeaderCell(props: HeaderColumn.HeaderFormatterProps) {
        return <span className={className} data-tb-test-id={TEXT_HEADER_TESTID}>{props.col.headerText}</span>;
      },
      textHeaderCellStyleOverrides,
    );
  }

  /** CapabilitiesHeaderCell - Formatting header cells that display capabilities as icons */
  export const CAPABILITIES_HEADER_TESTID = 'permissions-grid-capabilities-header';
  export const CAPABILITIES_HEADER_SECTION_TESTID = 'permissions-grid-capabilities-header-section';
  export const CAPABILITIES_HEADER_CAPABILITY_ICON_TESTID = 'permissions-grid-capabilities-header-capability-icon';

  export function CapabilitiesHeaderCell(capabilitySections: ReadonlyArray<CapabilitySection>, disabled: boolean): React.StatelessComponent<HeaderColumn.HeaderFormatterProps> {
    const styleOverrides: React.CSSProperties = { height: `${PermissionsGridStyles.RowHeight}px` };
    // first section doesn't need left divider line
    const firstSectionClassName = PermissionsGridStyles.CapabilitiesHeaderSection;
    const sectionClassName = PermissionsGridStyles.CapabilitiesHeaderSectionWithLeftDivider;
    // first icon container in a section needs smaller left margin
    const firstIconContainerClassName = PermissionsGridStyles.CapabilitiesHeaderIconContainerFirst;
    const iconContainerClassName = PermissionsGridStyles.CapabilitiesHeaderIconContainer;

    function capabilityIcon(capability: ICapabilityInfo, iconContainerClassName: string): JSX.Element {
      const capabilityDisplayText: string = capability.message();
      const capabilityImage: JSX.Element = <img
        alt={capabilityDisplayText}
        className={PermissionsGridStyles.CapabilitiesHeaderCapabilityIcon}
        src={disabled ? capability.disabledIcon : capability.icon} />;
      return (
        <div
          className={iconContainerClassName}
          data-tb-test-id={`${CAPABILITIES_HEADER_CAPABILITY_ICON_TESTID}-${capability.name}`}
          key={capability.name}
        >{disabled ? capabilityImage :
          <Tooltip
            followCursor={true}
            getContent={() => { return <span>{capabilityDisplayText}</span>; }}
            position={TooltipPosition.BottomRightCorner}
          >
            {capabilityImage}
          </Tooltip>
          }</div>
      );
    }

    return StyledHeaderFormatter(
      function CapabilitiesHeaderCell() {
        return (
          <div
            className={PermissionsGridStyles.CapabilitiesHeader}
            data-tb-test-id={CAPABILITIES_HEADER_TESTID}
          >{
              capabilitySections.map((section: CapabilitySection, index: number) => {
                return (
                  <div
                    className={index === 0 ? firstSectionClassName : sectionClassName}
                    data-tb-test-id={`${CAPABILITIES_HEADER_SECTION_TESTID}-${section.name}`}
                    key={section.name}
                  >{
                      section.capabilities.map((capability: ICapabilityInfo, indexInSection: number) => {
                        return capabilityIcon(capability, indexInSection === 0 ? firstIconContainerClassName : iconContainerClassName);
                      })
                    }</div>
                );
              })
            }</div>
        );
      },
      styleOverrides,
    );
  }

  /** GranteeCell - Formatting cells in the Groups/Users column  */
  export const GRANTEE_TESTID = 'permissions-grid-grantee';
  export const GRANTEE_ICON_TESTID = 'permissions-grid-grantee-icon';
  export const GRANTEE_NAME_TESTID = 'permissions-grid-grantee-name';

  export interface IGrantee {
    isInheritedProjectLeader?: boolean;
    isProjectLeader?: boolean;
    isUser: boolean;
    name: string;
    fullyQualifiedName: string;
    rowStatus: RowStatus;
  }

  function GranteeInfoCell(isExplicit: boolean, granteeData: IGrantee): JSX.Element {
    const disabled = granteeData.rowStatus === RowStatus.DISABLED;
    let icon: string;
    let ariaLabel: string;
    if (isExplicit && (granteeData.isProjectLeader || granteeData.isInheritedProjectLeader)) {
      icon = disabled ? PermissionsGridStyles.GranteeIconProjectLeaderDisabled : PermissionsGridStyles.GranteeIconProjectLeader;
      ariaLabel = Messages.permissionsDialogProjectLeaderIconLabel();
    } else if (granteeData.isUser) {
      icon = disabled ? PermissionsGridStyles.GranteeIconUserDisabled : PermissionsGridStyles.GranteeIconUser;
      ariaLabel = Messages.permissionsDialogUserIconLabel();
    } else {
      icon = disabled ? PermissionsGridStyles.GranteeIconGroupDisabled : PermissionsGridStyles.GranteeIconGroup;
      ariaLabel = Messages.permissionsDialogGroupIconLabel();
    }

    return (
      <span data-tb-test-id={GRANTEE_TESTID} className={PermissionsGridStyles.GranteeInfoCellContainer}>
        <span data-tb-test-id={GRANTEE_ICON_TESTID} className={PermissionsGridStyles.GranteeCellImgContainer} >
          <img
            aria-hidden={!isExplicit}
            aria-label={ariaLabel}
            className={PermissionsGridStyles.GranteeCellImg}
            src={icon}
          />
        </span>
        <span
          data-tb-test-id={GRANTEE_NAME_TESTID}
          className={disabled ? PermissionsGridStyles.GranteeCellTextDisabled : PermissionsGridStyles.GranteeCellText}
          title={granteeData.fullyQualifiedName}
        >
          {granteeData.name}
        </span>
      </span>
    );
  }

  export function GranteeCell(isExplicit: boolean, rowToGranteeData: (row: DataGrid.Row) => IGrantee): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function GranteeCell(props: BodyCell.Props) {
        const granteeData = rowToGranteeData(props.row);
        const isSelected = granteeData.rowStatus === RowStatus.SELECTED;
        let containerClass = '';
        if (isExplicit) {
          containerClass = PermissionsGridStyles.GranteeCellContainerExplicit;
        }
        if (isSelected) {
          containerClass = isExplicit ? PermissionsGridStyles.GranteeCellContainerExplicitSelected : PermissionsGridStyles.GranteeCellContainerSelected;
        } else {
          containerClass = `${PermissionsGridStyles.HoverableGranteeCellClass} ${containerClass}`;
        }
        return (
          <div className={containerClass}>
            {GranteeInfoCell(isExplicit, granteeData)}
            {granteeData.rowStatus === RowStatus.RELEVANT && <div className={PermissionsGridStyles.GranteeCellStripe} />}
          </div>
        );
      },
      { padding: 0, }
    );
  }

  /** EditableGranteeCell - Similar to  DataGridCells/ActionsCell and ActionsMenu but not tied to ListView */
  export const GRANTEE_EDITABLE_TESTID = 'permissions-grid-grantee-editable';
  export const GRANTEE_EDITABLE_BUTTON_PANEL_TESTID = 'permissions-grid-grantee-edit-button-panel';
  export const GRANTEE_EDITABLE_CANCEL_BUTTON_TESTID = 'permissions-grid-grantee-cancel-button';
  export const GRANTEE_EDITABLE_DELETE_BUTTON_TESTID = 'permissions-grid-grantee-delete-button';
  export const GRANTEE_EDITABLE_SAVE_BUTTON_TESTID = 'permissions-grid-grantee-save-button';
  export const MENU_ITEM_VALUE_COPY = 'copy';
  export const MENU_ITEM_VALUE_PASTE = 'paste';
  export const MENU_ITEM_VALUE_SET_PL = 'set-pl';
  export const MENU_ITEM_VALUE_REMOVE_PL = 'remove-pl';
  export const MENU_ITEM_VALUE_ENABLE_PL = 'enable-pl';
  export const MENU_ITEM_VALUE_DELETE = 'delete';

  export function EditableGranteeCell(
    onCancelEditMode: () => void,
    onSave: () => void,
    onCopyPermissions: (rowIndex: number) => void,
    onPastePermissions: (rowIndex: number) => void,
    onUpdateProjectLeader: (rowIndex: number, originalPermission: Permission) => void,
    onGranteeSelect: (group?: IGroup, user?: IUser) => void,
    onDelete: (rowIndex?: number) => void,
    disabledPaste: boolean,
    rowToGranteeData: (row: DataGrid.Row) => IGrantee,
    authorizableType: AuthorizableType,
    disabledDelete: boolean,
    isLoading: boolean
  ): React.StatelessComponent<BodyCell.Props> {
    function renderButtonContent(isActive: boolean, disabled: boolean): JSX.Element {
      return (
        <Icons.actionIcon
          disabled={disabled}
          hovered={isActive}
          style={{ backgroundColor: 'transparent', marginRight: `${PermissionsGridStyles.EmptySpaceMargin}px`, verticalAlign: 'middle', }}
          suppressHover={disabled}
          title={Messages.moreActions()}
        />
      );
    }

    function renderNotAllowedReason(isUser: boolean): JSX.Element {
      return (
        <span>
          {isUser ? Messages.inherited_project_leader_tooltip_action_menu_user() : Messages.inherited_project_leader_tooltip_action_menu_group()}
        </span>
      );
    }

    function handleMenuItemSelected(menuItemValue: string, rowIndex: number): void {
      switch (menuItemValue) {
        case MENU_ITEM_VALUE_COPY:
          onCopyPermissions(rowIndex);
          break;
        case MENU_ITEM_VALUE_PASTE:
          onPastePermissions(rowIndex);
          break;
        case MENU_ITEM_VALUE_SET_PL:
          onUpdateProjectLeader(rowIndex, Permission.Unspecified);
          break;
        case MENU_ITEM_VALUE_REMOVE_PL:
          onUpdateProjectLeader(rowIndex, Permission.Allowed);
          break;
        case MENU_ITEM_VALUE_ENABLE_PL:
          onUpdateProjectLeader(rowIndex, Permission.Denied);
          break;
        case MENU_ITEM_VALUE_DELETE:
          onDelete(rowIndex);
          break;
      }
    }

    function renderActionMenuPanelContent(flyoutProps: ExplorerFlyoutPanelProps, rowIndex: number, inEditMode: boolean, isProjectLeader?: boolean): JSX.Element {
      const copyMenuItem: MenuItemInfo = {
        disabled: inEditMode || isProjectLeader !== undefined,
        displayValue: Messages.permissionsCopy(),
        value: MENU_ITEM_VALUE_COPY,
      };

      const pasteMenuItem: MenuItemInfo = {
        disabled: disabledPaste || isProjectLeader !== undefined,
        displayValue: Messages.permissionsPaste(),
        value: MENU_ITEM_VALUE_PASTE,
      };

      const deleteMenuItem: MenuItemInfo = {
        disabled: disabledDelete,
        displayValue: Messages.permissionsDeleteRule(),
        value: MENU_ITEM_VALUE_DELETE,
      };

      const setPLMenuItem: MenuItemInfo = {
        displayValue: Messages.permissionsSetProjectLeaderAction(),
        value: MENU_ITEM_VALUE_SET_PL,
      };

      const removePLMenuItem: MenuItemInfo = {
        displayValue: Messages.permissionsRemoveAsProjectLeader(),
        value: MENU_ITEM_VALUE_REMOVE_PL,
      };

      const enablePLMenuItem: MenuItemInfo = {
        displayValue: Messages.permissionsEnableProjectLeader(),
        value: MENU_ITEM_VALUE_ENABLE_PL,
      };

      const dividerMenuItem: MenuItemInfo = {
        isDivider: true,
      };

      const isProjectLeaderOptionAvailableForCurrentAuthType = (currentAuthType: AuthorizableType): boolean => {
        // Project leader is available only when you open permission dialog for a project
        switch (currentAuthType) {
          case AuthorizableType.PROJECT:
          case AuthorizableType.PROJECT_DATASOURCE_TEMPLATE:
          case AuthorizableType.PROJECT_DATA_ROLE_TEMPLATE:
          case AuthorizableType.PROJECT_FLOW_TEMPLATE:
          case AuthorizableType.PROJECT_METRIC_TEMPLATE:
          case AuthorizableType.PROJECT_PUBLISHED_CONNECTION_TEMPLATE:
          case AuthorizableType.PROJECT_WORKBOOK_TEMPLATE:
            return true;
        }
        return false;
      };

      const handleItemSelected = (menuItemValue: string): void => {
        handleMenuItemSelected(menuItemValue, rowIndex);
      };

      let menuItems = [copyMenuItem, pasteMenuItem];

      if (isProjectLeaderOptionAvailableForCurrentAuthType(authorizableType)) {
        switch (isProjectLeader) {
          case true:
            menuItems.push(removePLMenuItem);
            break;
          case false:
            menuItems.push(enablePLMenuItem);
            break;
          case undefined:
            menuItems.push(setPLMenuItem);
            break;
        }
      }

      menuItems.push(dividerMenuItem, deleteMenuItem);

      const panelProps: ButtonControlledFlyoutListMenuProps = {
        anchorElement: flyoutProps.anchorEl,
        closeMe: flyoutProps.onCloseRequest,
        menuItems,
        onComponentDidMount: () => { },
        onMenuItemSelected: handleItemSelected,
        testId: flyoutProps.testId,
      };

      return <ButtonControlledFlyoutListMenu {...panelProps} />;
    }


    function renderEditButtons(isClean: boolean, isDelete: boolean): JSX.Element {
      let spinner: JSX.Element = (
        <ActivitySpinnerWidget
          onDarkBackground={true}
          spinnerSize={ActivitySpinnerSize.Small}
          testId={'test'}
        />
      );
      const actionButton: JSX.Element = isDelete ?
        (
          <ButtonWidget
            buttonType={ButtonType.Destructive}
            children={isLoading ? spinner : Messages.permissionsDeleteRuleButton()}
            className={PermissionsGridStyles.EditableGranteeCellButton}
            disabled={isLoading}
            handleClick={onSave}
            testId={GRANTEE_EDITABLE_DELETE_BUTTON_TESTID}
          />
        )
        :
        (
          <ButtonWidget
            buttonType={ButtonType.Go}
            children={isLoading ? spinner : Messages.save()}
            className={PermissionsGridStyles.EditableGranteeCellButton}
            disabled={isClean || isLoading}
            handleClick={onSave}
            testId={GRANTEE_EDITABLE_SAVE_BUTTON_TESTID}
          />
        );
      return (
        <div
          className={PermissionsGridStyles.EditableGranteeCellButtonPanel}
          data-tb-test-id={GRANTEE_EDITABLE_BUTTON_PANEL_TESTID}
        >
          <ButtonWidget
            buttonType={ButtonType.Outline}
            children={Messages.cancel()}
            className={PermissionsGridStyles.EditableGranteeCellCancelButton}
            disabled={isLoading}
            handleClick={onCancelEditMode}
            testId={GRANTEE_EDITABLE_CANCEL_BUTTON_TESTID}
          />
          {actionButton}
        </div>
      );
    }

    return StyledCellFormatter(
      function EditableGranteeCell(props: BodyCell.Props) {
        const granteeData = rowToGranteeData(props.row);
        const inEditMode = isRowInEditMode(granteeData.rowStatus);
        const fullyDisabled = granteeData.rowStatus === RowStatus.DISABLED;
        const notEditable = fullyDisabled || granteeData.isInheritedProjectLeader;
        const useSelectedBackgroundColor = inEditMode || granteeData.rowStatus === RowStatus.SELECTED;
        const renderButton = (isActive: boolean): JSX.Element => {
          return renderButtonContent(isActive, notEditable);
        };
        const renderActionMenuPanel = (panelProps: ExplorerFlyoutPanelProps): JSX.Element => {
          return renderActionMenuPanelContent(panelProps, props.rowIdx, inEditMode, granteeData.isProjectLeader);
        };
        const renderEditPanel = (): JSX.Element => {
          return inEditMode && renderEditButtons(granteeData.rowStatus === RowStatus.EDIT_CLEAN, granteeData.rowStatus === RowStatus.EDIT_DELETE);
        };

        const renderGranteeInfoAndActionMenu = (): JSX.Element => {
          const containerClass = useSelectedBackgroundColor ? PermissionsGridStyles.EditableGranteeCellContainerSelected : `${PermissionsGridStyles.HoverableGranteeCellClass} ${PermissionsGridStyles.EditableGranteeCellContainer}`;
          const notAllowed = !fullyDisabled && granteeData.isInheritedProjectLeader;
          const explorerDropdownProps: ExplorerDropdownProps = {
            buttonStyleOverrides: {
              height: 'auto',
              minWidth: 0,
              verticalAlign: 'middle',
              backgroundColor: 'transparent',
            },
            buttonTitle: notEditable ? undefined : Messages.moreActions(),
            disabled: notEditable,
            testId: ActionsMenuTestId,
            renderButtonContent: renderButton,
            renderPanel: renderActionMenuPanel,
            panelTitle: !notEditable && Messages.actionsMenu(),
            renderButtonContentOnly: true,
          };
          return (
            <div data-tb-test-id={GRANTEE_EDITABLE_TESTID} className={containerClass}>
              <div className={PermissionsGridStyles.EditableGranteeCellSubContainer}>
                {GranteeInfoCell(true, granteeData)}
              </div>
              <Tooltip
                position={TooltipPosition.BottomRightCorner}
                getContent={() => { return notAllowed ? renderNotAllowedReason(granteeData.isUser) : null; }}
              >
                <span className={notAllowed ? PermissionsGridStyles.NotAllowedPermissionCellContainer : ''}>{renderDropdown(notEditable, renderButtonContent(false, notEditable), explorerDropdownProps)}</span>
              </Tooltip>
            </div>
          );
        };
        const renderGranteeSearchField = (): JSX.Element => {
          return <GranteeSearchField onGranteeSelect={onGranteeSelect} />;
        };

        return (
          <div>
            {granteeData.name ? renderGranteeInfoAndActionMenu() : renderGranteeSearchField()}
            {granteeData.rowStatus === RowStatus.RELEVANT && <div className={PermissionsGridStyles.GranteeCellStripe} />}
            {renderEditPanel()}
          </div>
        );
      },
      { padding: 0 }
    );
  }

  /** PermissionRoleCell - Formatting cells in the Permission Role column  */
  export const PermissionRoleDisplayText: { [n: string]: () => string } = {
    [PermissionRoleName.Administer]: Messages.permission_role_administer,
    [PermissionRoleName.Custom]: Messages.custom,
    [PermissionRoleName.Denied]: Messages.denied,
    [PermissionRoleName.Explore]: Messages.permission_role_explore,
    [PermissionRoleName.None]: Messages.none,
    [PermissionRoleName.Publish]: Messages.permission_role_publish,
    [PermissionRoleName.View]: Messages.permission_role_view,
  };
  export const PERMISSION_ROLE_TESTID = 'permissions-grid-permission-role';
  export const PERMISSION_ROLE_DROPDOWN_TESTID = 'permissions-grid-permission-role-dropdown';

  export interface IPermissionRole {
    isProjectLeader: boolean;
    isUser: boolean;
    permissionRole: string;
    permissionRoleOptions: string[];
    rowStatus: RowStatus;
  }

  export function PermissionRoleCell(
    handlePermissionRoleChange: (rowIndex: number, newValue: string) => void,
    rowToPermissionRoleData: (row: DataGrid.Row) => IPermissionRole,
    isEditable?: boolean,
  ): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PermissionRoleCell(props: BodyCell.Props) {
        const permissionRoleData = rowToPermissionRoleData(props.row);
        const permissionRole = permissionRoleData.permissionRole;
        const rowStatus = permissionRoleData.rowStatus;
        const disabled = rowStatus === RowStatus.DISABLED;
        if (!isEditable) {
          // only render permission role as text
          return <span className={disabled ? PermissionsGridStyles.DisabledTextStyle : ''} data-tb-test-id={PERMISSION_ROLE_TESTID}>{PermissionRoleDisplayText[permissionRole]()}</span>;
        }

        const notInteractive = disabled || permissionRoleData.isProjectLeader;
        const notAllowed = permissionRoleData.isProjectLeader && !disabled;
        const highlightable = [RowStatus.SELECTED, RowStatus.EDIT_CLEAN, RowStatus.EDIT_DIRTY, RowStatus.EDIT_DELETE]
          .includes(rowStatus);

        let containerClassName = PermissionsGridStyles.PermissionRoleCellContainer;
        if (notAllowed) {
          containerClassName = `${PermissionsGridStyles.NotAllowedPermissionCellContainer} ${containerClassName}`;
        }
        if (highlightable) {
          containerClassName = `${PermissionsGridStyles.SelectedPermissionRoleCellContainer} ${containerClassName}`;
        }

        function renderButtonContent(showingOrActive: boolean, disabled: boolean): JSX.Element {
          let textClassName = PermissionsGridStyles.PermissionRoleCellText;
          if (disabled) {
            textClassName += ` ${PermissionsGridStyles.DisabledTextStyle}`;
          }
          return (
            <div
              className={highlightable ? PermissionsGridStyles.PermissionsRoleCellSubContainerSelected : PermissionsGridStyles.PermissionRoleCellSubContainerBase}
            >
              <span className={textClassName} >{PermissionRoleDisplayText[permissionRole]()}</span>
              <Img src={DropdownArrowIcon.icon({ hover: showingOrActive, active: showingOrActive, disabled, })} />
            </div>
          );
        }

        function renderPanel(flyoutProps: ExplorerFlyoutPanelProps): JSX.Element {
          const handleItemSelected = (menuItemValue: string): void => {
            handlePermissionRoleChange(props.rowIdx, menuItemValue);
          };

          const permissionRoleMenuItems: MenuItemInfo[] = permissionRoleData.permissionRoleOptions
            .map(permissionRoleOption => {
              return {
                displayValue: PermissionRoleDisplayText[permissionRoleOption](),
                value: permissionRoleOption,
              } as MenuItemInfo;
            });

          const panelProps: ButtonControlledFlyoutListMenuProps = {
            anchorElement: flyoutProps.anchorEl,
            closeMe: flyoutProps.onCloseRequest,
            menuItems: permissionRoleMenuItems,
            onComponentDidMount: () => { },
            onMenuItemSelected: handleItemSelected,
            testId: flyoutProps.testId,
            menuStyle: {
              width: `${PermissionsGridStyles.RoleCellWidth}px`,
              marginLeft: '-3px'
            },
          };

          return <ButtonControlledFlyoutListMenu {...panelProps} />;
        }

        function tooltipContent(): JSX.Element {
          return notAllowed && (<span>{permissionRoleData.isUser ? Messages.permission_role_reason_user_project_leader() : Messages.permission_role_reason_group_project_leader()}</span>);
        }
        const explorerDropdownProps: ExplorerDropdownProps = {
          buttonStyleOverrides: {
            height: 'auto',
            minWidth: 0,
            verticalAlign: 'middle',
          },
          disabled: notInteractive,
          renderButtonContent,
          renderPanel,
          testId: PERMISSION_ROLE_TESTID,
          panelTitle: '',
          renderButtonContentOnly: true,
        };

        return (
          <div className={containerClassName}>
            <Tooltip
              getContent={tooltipContent}
              position={TooltipPosition.BottomRightCorner}
            >
              <div
                className={notAllowed ? PermissionsGridStyles.NotAllowedPermissionCellContainer : ''}
                data-tb-test-id={PERMISSION_ROLE_DROPDOWN_TESTID}
              >
                {renderDropdown(notInteractive, renderButtonContent(false, notInteractive), explorerDropdownProps)}
              </div>
            </Tooltip>
          </div>
        );
      },
      // Datagrid cell wrapper has padding of 12 px as well as the cell has padding of 12px. We need to remove one
      PermissionsGridStyles.DataGridCellOverrideRemovePadding
    );
  }

  /** SiteRoleCell - Formatting cells in the Site Role column  */
  export const SITE_ROLE_TESTID = 'permissions-grid-site-role';

  export interface ISiteRole {
    siteRole: string;
    rowStatus: RowStatus;
  }

  export function SiteRoleCell(rowToSiteRoleData: (row: DataGrid.Row) => ISiteRole): React.StatelessComponent<BodyCell.Props> {

    return StyledCellFormatter(
      function SiteRoleCell(props: BodyCell.Props) {
        const siteRoleData = rowToSiteRoleData(props.row);
        const siteRole = siteRoleData.siteRole;
        const siteRoleName = SiteRolesHelper.siteRoleToName(siteRole) || '';
        const className = siteRoleData.rowStatus === RowStatus.DISABLED ?
          PermissionsGridStyles.SiteRoleTextDisabled :
          PermissionsGridStyles.SiteRoleCellText;
        return <div className={className} data-tb-test-id={SITE_ROLE_TESTID} title={siteRoleName} >{siteRoleName}</div>;
      },
      PermissionsGridStyles.DataGridCellOverrideRemovePadding
    );
  }

  /** CapabilitiesCell - Formatting cells in the capabilities column  */
  export const CAPABILITIES_TESTID = 'permissions-grid-capabilities';
  export const CAPABILITIES_SECTION_TESTID = 'permissions-grid-capabilities-section';
  export const CAPABILITIES_PERMISSION_TESTID = 'permissions-grid-capabilities-permission';

  export const PermissionCapabilitiesCellReason: { [n: string]: () => string } = {
    [PermissionReason.GroupAllow]: Messages.permission_reason_group_allow,
    [PermissionReason.GroupDeny]: Messages.permission_reason_group_disallow,
    [PermissionReason.GroupProjectLeaderAllow]: Messages.permission_reason_group_project_leader,
    [PermissionReason.GuestAllow]: Messages.permission_reason_guest_allowed,
    [PermissionReason.DerivedAllow]: Messages.permission_reason_derived_allow,
    [PermissionReason.DerivedWriteAllow]: Messages.permission_reason_derived_allow,
    [PermissionReason.ImplicitDeny]: Messages.permission_reason_implicit_deny,
    [PermissionReason.Mixed]: Messages.permission_reason_mixed,
    [PermissionReason.Unspecified]: Messages.permission_reason_unspecified,
    [PermissionReason.UserAdminAllow]: Messages.permission_reason_user_admin,
    [PermissionReason.UserAllow]: Messages.permission_reason_user_allow,
    [PermissionReason.UserAuthorizableOwnerAllow]: Messages.permission_reason_user_authorizable_owner,
    [PermissionReason.UserDeny]: Messages.permission_reason_user_disallow,
    [PermissionReason.UserLicensingDeny]: Messages.permission_reason_user_licensing,
    [PermissionReason.UserProjectLeaderAllow]: Messages.permission_reason_user_project_leader,
    [PermissionReason.UserProjectOwnerAllow]: Messages.permission_reason_user_project_owner,
  };

  export const PermissionCapabilitiesCellValue: { [n: string]: () => string } = {
    [Permission.Allowed]: Messages.permission_value_allowed,
    [Permission.Denied]: Messages.permission_value_denied,
    [Permission.Mixed]: Messages.permission_value_mixed,
    [Permission.Unspecified]: Messages.permission_value_unspecified,
  };

  export interface ICapabilities {
    capabilities: _.Dictionary<IPermission>; // keyed by capability.name
    capabilitiesSections: ReadonlyArray<CapabilitySection>;
    isExplicit: boolean;
    isProjectLeader: boolean;
    rowStatus: RowStatus;
    editabilityDescription?: string;
  }

  export function CapabilitiesCell(
    handleCapabilityCellClick: (capabilityName: string, rowIndex: number) => void,
    rowToCapabilitiesData: (row: DataGrid.Row) => ICapabilities,
    isEditable?: boolean,
  ): React.StatelessComponent<BodyCell.Props> {
    function tooltipContent(capability: string, reason: string, nonEditableReason: string): JSX.Element {
      const capabilityDescription = Messages.permissionsCapabilityReason({
        CAPABILITY: capability,
        REASON: reason,
      });
      return (
        <span>
          <span>{capabilityDescription}</span>
          {nonEditableReason && <span><br/>{nonEditableReason}</span>}
        </span>
      );
    }

    function explicitCell(
      rowIndex: number,
      permission: IPermission,
      capability: ICapabilityInfo,
      capabilityContainerClassName: string,
      disabled: boolean,
      rowInEditMode: boolean,
      isProjectLeader: boolean,
      editabilityDescription: string
    ): JSX.Element {
      let backgroundColor: string;
      let icon: string;
      let reason: string;
      let isMixed = false;
      const disabledOrProjectLeader = disabled || isProjectLeader;
      if (permission) {
        switch (permission.permission) {
          case Permission.Allowed:
            backgroundColor = disabled ? PermissionsGridStyles.CapabilitiesCellAllowedCapabilityDisabled : PermissionsGridStyles.CapabilitiesCellAllowedCapability;
            icon = PermissionsGridStyles.ExplicitAllow;
            if (isProjectLeader) {
              reason = permission.reason === PermissionReason.UserProjectLeaderAllow ? Messages.permission_reason_user_allow_project_leader() : Messages.permission_reason_group_allow_project_leader();
            } else {
              reason = Messages.permission_value_allowed();
            }
            break;
          case Permission.Denied:
            backgroundColor = disabled ? PermissionsGridStyles.CapabilitiesCellDeniedCapabilityDisabled : PermissionsGridStyles.CapabilitiesCellDeniedCapability;
            icon = PermissionsGridStyles.ExplicitDenied;
            reason = Messages.permission_value_denied();
            break;
          case Permission.Mixed:
            backgroundColor = disabled ? PermissionsGridStyles.CapabilitiesCellMixedCapabilityDisabled : PermissionsGridStyles.CapabilitiesCellMixedCapability;
            icon = PermissionsGridStyles.ExplicitMixed;
            reason = Messages.permission_value_mixed();
            isMixed = true;
            break;
          case Permission.Unspecified:
          default:
            if (rowInEditMode) {
              backgroundColor = PermissionsGridStyles.EditableCapabilitiesCellUnspecifiedCapability;
            } else {
              backgroundColor = disabled ? PermissionsGridStyles.CapabilitiesCellUnspecifiedCapabilityDisabled : PermissionsGridStyles.CapabilitiesCellUnspecifiedCapability;
            }
            reason = Messages.permission_value_unspecified();
            break;
        }
      } else {
        if (rowInEditMode) {
          backgroundColor = PermissionsGridStyles.EditableCapabilitiesCellUnspecifiedCapability;
        } else {
          backgroundColor = disabled ? PermissionsGridStyles.CapabilitiesCellUnspecifiedCapabilityDisabled : PermissionsGridStyles.CapabilitiesCellUnspecifiedCapability;
        }
        reason = Messages.permission_value_unspecified();
      }

      const capabilityCellClass = rowInEditMode ? PermissionsGridStyles.EditableCapabilitiesCellCapability : PermissionsGridStyles.CapabilitiesCellCapability;
      let containerClasses = `${capabilityCellClass} ${backgroundColor}`;
      if ((isProjectLeader || !isEditable) && !disabled) {
        capabilityContainerClassName = `${capabilityContainerClassName} ${PermissionsGridStyles.NotAllowedPermissionCellContainer}`;
      } else if (isEditable && !disabledOrProjectLeader) {
        containerClasses = `${containerClasses} ${PermissionsGridStyles.HoverableCapabilitiesCellContainer}`;
      }
      const imgClassName = isMixed ? PermissionsGridStyles.CapabilitiesCellImgMixed : PermissionsGridStyles.CapabilitiesCellImg;
      return capabilityCell(rowIndex, containerClasses, icon, capability, reason, capabilityContainerClassName, imgClassName, disabled, isProjectLeader, editabilityDescription);
    }

    function effectiveCell(
      rowIndex: number,
      permission: IPermission,
      capability: ICapabilityInfo,
      capabilityContainerClassName: string,
      disabled: boolean,
      rowInEditMode: boolean,
      notInteractive: boolean,
      editabilityDescription: string
    ): JSX.Element {
      let icon;
      let reason;
      let isMixed = false;
      if (permission) {
        switch (permission.permission) {
          case Permission.Allowed:
            icon = disabled ? PermissionsGridStyles.EffectiveAllowDisabled : PermissionsGridStyles.EffectiveAllow;
            break;
          case Permission.Mixed:
            icon = disabled ? PermissionsGridStyles.EffectiveMixedDisabled : PermissionsGridStyles.EffectiveMixed;
            isMixed = true;
            break;
          case Permission.Denied:
          case Permission.Unspecified:
          default:
            icon = disabled ? PermissionsGridStyles.EffectiveDeniedDisabled : PermissionsGridStyles.EffectiveDenied;
            break;
        }
        if (permission.reason !== PermissionReason.Mixed && permission.reason !== PermissionReason.Unspecified) {
          reason = PermissionCapabilitiesCellReason[permission.reason]();
        } else {
          // When permissioning multiple pieces of content, a capability can have 1 value but 2 different reasons (so mixed reason).
          // If the permission reason is mixed, display the permission value instead to make it obvious if the user
          // has denied/allowed/mixed for this capability
          reason = PermissionCapabilitiesCellValue[permission.permission]();
        }
      } else {
        icon = disabled ? PermissionsGridStyles.EffectiveDeniedDisabled : PermissionsGridStyles.EffectiveDenied;
        reason = PermissionCapabilitiesCellReason[PermissionReason.Unspecified]();
      }
      const imgClassName = isMixed ? PermissionsGridStyles.CapabilitiesCellImgMixed : PermissionsGridStyles.CapabilitiesCellImg;
      return capabilityCell(rowIndex, PermissionsGridStyles.CapabilitiesCellCapability, icon, capability, reason, capabilityContainerClassName, imgClassName, disabled, notInteractive, null);
    }

    function capabilityCell(
      rowIndex: number,
      containerClasses: string,
      icon: string,
      capability: ICapabilityInfo,
      reason: string,
      capabilityContainerClassName: string,
      imgClassName: string,
      disabled: boolean,
      notInteractive: boolean,
      nonEditableReason: string
    ): JSX.Element {
      /* eslint-disable jsx-a11y/alt-text */
      const capabilityBox: JSX.Element = (
        <div className={containerClasses}>
          {icon &&
            <img
              aria-hidden={true}
              className={imgClassName}
              src={icon}
            />
          }
        </div>
      );
    /* eslint-disable jsx-a11y/click-events-have-key-events */
    return (
        <span
          className={capabilityContainerClassName}
          data-tb-test-id={`${CAPABILITIES_PERMISSION_TESTID}-${capability.name}`}
          key={capability.name}
          onClick={disabled || notInteractive || !isEditable ? null : () => { handleCapabilityCellClick(capability.name, rowIndex); }}
        >{disabled ? capabilityBox :
          <Tooltip
            getContent={() => { return tooltipContent(capability.message(), reason, nonEditableReason); }}
            position={TooltipPosition.BottomRightCorner}
          >
            {capabilityBox}
          </Tooltip>
          }</span>
      );
    }

    function getSection(
      rowIndex: number,
      section: CapabilitySection,
      styleClassName: string,
      cell: (rowIndex: number, permission: IPermission, capability: ICapabilityInfo, capabilityContainerClassName: string, disabled: boolean, inEditMode: boolean, notInteractive: boolean, editabilityDescription: string) => JSX.Element,
      capabilitiesData: ICapabilities,
      disabled: boolean,
      rowInEditMode: boolean,
      notInteractive: boolean
    ): JSX.Element {
      return (
        <span className={styleClassName} data-tb-test-id={`${CAPABILITIES_SECTION_TESTID}-${section.name}`} key={section.name}>
          {section.capabilities.map((capability: ICapabilityInfo, indexInSection: number) => {
            const permission = capabilitiesData.capabilities && capabilitiesData.capabilities[capability.name];
            // first capability container in a section doesn't need left margin
            const capabilityContainerClassName = indexInSection === 0 ? PermissionsGridStyles.CapabilitiesCellCapabilityContainerBase : PermissionsGridStyles.CapabilitiesCellCapabilityContainerWithLeftMargin;
            return cell(rowIndex, permission, capability, capabilityContainerClassName, disabled, rowInEditMode, notInteractive, capabilitiesData.editabilityDescription);
          })
          }
        </span>
      );
    }

    return StyledCellFormatter(
      function CapabilitiesCell(props: BodyCell.Props) {
        const capabilitiesData = rowToCapabilitiesData(props.row);
        const rowStatus = capabilitiesData.rowStatus;
        const cell = capabilitiesData.isExplicit ? explicitCell : effectiveCell;
        const disabled = rowStatus === RowStatus.DISABLED;
        const rowInEditMode = isRowInEditMode(rowStatus);
        return (
          <span data-tb-test-id={CAPABILITIES_TESTID}>
            {capabilitiesData.capabilitiesSections.map((section: CapabilitySection, index: number) => {
              // first section doesn't need left padding
              const className = index > 0 ? PermissionsGridStyles.CapabilitiesCellSection : '';
              return getSection(props.rowIdx, section, className, cell, capabilitiesData, disabled, rowInEditMode, capabilitiesData.isProjectLeader);
            })}
          </span>
        );
      },
      { overflow: 'initial', padding: '0px', textOverflow: 'initial' }
    );
  }
}
